/**
 * SalesTab — Ventas recibidas de los terminales (PLAN_POS_IMPLEMENTACION F3).
 *
 * La venta llega ya impresa y entregada: esta pantalla no aprueba nada, muestra
 * qué pasó con cada boleta después. Lo que pide atención son dos estados:
 * «En conflicto» (el timbre no verificó, o la nota de crédito no cabe en su
 * original; no se envió al SII) y
 * «Rechazada» (el SII la rechazó). Por eso van arriba, como avisos que filtran.
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react'
import { Button } from '@/components/Button'
import { DataTable } from '@/components/ds/DataTable'
import { SelectField } from '@/components/ds/SelectField'
import { StatusBadge } from '@/components/ds/StatusBadge'
import { POS_SALE_STATUS } from '@/components/ds/statusCatalogs'
import { EmptyState } from '@/components/EmptyState'
import { formatMoneyForTenant } from '@/lib/locale'
import { fmtDate, fmtDateTime } from '@/lib/utils'
import { usePosSales, usePosTerminals, type PosSale } from '../hooks/usePos'

const TIPOS: Record<number, string> = { 33: 'Factura', 39: 'Boleta', 41: 'Boleta exenta', 61: 'Nota de crédito' }
const METODOS: Record<string, string> = {
  cash: 'Efectivo', debit: 'Débito', credit: 'Crédito', transfer: 'Transferencia', other: 'Otro',
}

/** Lo que explica el estado, en una línea: por qué quedó en conflicto o por qué se rechazó. */
function motivo(v: PosSale): string | null {
  if (v.status === 'conflict' && v.ted_problems.length) {
    const p = v.ted_problems[0]
    const extra = v.ted_problems.length > 1 ? ` (y ${v.ted_problems.length - 1} más)` : ''
    // Los motivos de una nota de crédito traen `code` y un mensaje que se explica solo.
    return `${p.code ? p.message : `${p.field}: ${p.message}`}${extra}`
  }
  if (v.status === 'rejected' && v.rejection) return v.rejection.message
  return null
}

export default function SalesTab() {
  const [estado, setEstado] = useState('')
  const [terminal, setTerminal] = useState('')
  const [page, setPage] = useState(1)
  const { data: terminales = [] } = usePosTerminals()
  const { data, isLoading } = usePosSales({ status: estado || undefined, terminal: terminal || undefined, page })

  const ventas = data?.sales ?? []
  const paginas = data ? Math.max(1, Math.ceil(data.count / data.page_size)) : 1
  const pendientes = data?.pending ?? { conflict: 0, rejected: 0 }
  const filtrar = (s: string) => { setEstado(s); setPage(1) }

  return (
    <>
      {(pendientes.conflict > 0 || pendientes.rejected > 0) && (
        <div className="mb-4 flex flex-wrap gap-2" role="status">
          {pendientes.conflict > 0 && (
            <button onClick={() => filtrar('conflict')}
              className="inline-flex items-center gap-2 rounded-lg border border-danger-border bg-danger-surface px-3 py-2 text-sm text-danger-content hover:opacity-90">
              <AlertTriangle size={14} />
              {pendientes.conflict === 1 ? '1 venta en conflicto' : `${pendientes.conflict} ventas en conflicto`}
              <span className="text-xs opacity-80">· no se enviaron al SII</span>
            </button>
          )}
          {pendientes.rejected > 0 && (
            <button onClick={() => filtrar('rejected')}
              className="inline-flex items-center gap-2 rounded-lg border border-danger-border bg-danger-surface px-3 py-2 text-sm text-danger-content hover:opacity-90">
              <AlertTriangle size={14} />
              {pendientes.rejected === 1 ? '1 boleta rechazada por el SII' : `${pendientes.rejected} boletas rechazadas por el SII`}
            </button>
          )}
        </div>
      )}

      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:max-w-2xl">
        <SelectField dense label="Estado" placeholder="Todos los estados" value={estado}
          onChange={(e) => filtrar(e.target.value)}
          options={Object.entries(POS_SALE_STATUS).map(([value, s]) => ({ value, label: s.label }))} />
        <SelectField dense label="Terminal" placeholder="Todos los terminales" value={terminal}
          onChange={(e) => { setTerminal(e.target.value); setPage(1) }}
          options={terminales.map((t) => ({ value: t.id, label: `${t.code} · ${t.store_name}` }))} />
      </div>

      <DataTable<PosSale>
        rows={ventas}
        rowKey={(v) => v.id}
        loading={isLoading}
        empty={<EmptyState
          title={estado || terminal ? 'No hay ventas con ese filtro.' : 'Todavía no llegan ventas de los terminales.'}
          hint={estado || terminal ? undefined : 'Cada boleta que una caja imprime aparece acá cuando el equipo tiene conexión.'}
          size="sm" />}
        columns={[
          { key: 'received', header: 'Recibida', render: (v) => (
            <span title={`Fecha de la boleta: ${fmtDate(v.issue_date)}`}>{fmtDateTime(v.received_at)}</span>
          ) },
          { key: 'terminal', header: 'Terminal', render: (v) => (
            <span><span className="font-mono">{v.terminal_code}</span>
              <span className="block text-xs text-content-subtle">{v.store_name}</span></span>
          ) },
          { key: 'doc', header: 'Documento', render: (v) => (
            <span>{TIPOS[v.document_type_code] ?? v.document_type_code}{' '}
              <span className="font-mono tabular-nums">N° {v.folio}</span></span>
          ) },
          { key: 'total', header: 'Total', numeric: true, render: (v) => formatMoneyForTenant(v.total) },
          { key: 'payments', header: 'Pagos', render: (v) => (
            <span className="text-sm text-content-muted">
              {v.payments.map((p) => METODOS[p.method] ?? p.method).join(' + ')}
            </span>
          ) },
          { key: 'status', header: 'Estado', render: (v) => {
            const m = motivo(v)
            return (
              <div className="min-w-0">
                <StatusBadge status={v.status} catalog={POS_SALE_STATUS} />
                {m && <span className="mt-1 block max-w-xs truncate text-xs text-danger-content" title={m}>{m}</span>}
                {v.warnings.map((w) => (
                  <span key={w.code} className="mt-1 block max-w-xs truncate text-xs text-warning-content" title={w.message}>
                    {w.message}
                  </span>
                ))}
              </div>
            )
          } },
          { key: 'actions', header: '', render: (v) => v.document_id ? (
            <div className="flex justify-end">
              <Link to={`/sales/documents/${v.document_id}/360`} title="Ver el documento"
                aria-label={`Ver la boleta ${v.folio}`}
                className="p-1.5 rounded-lg hover:bg-soft text-content-subtle hover:text-content">
                <ExternalLink size={14} />
              </Link>
            </div>
          ) : null },
        ]}
      />

      {paginas > 1 && (
        <div className="mt-4 flex items-center justify-end gap-3 text-sm text-content-muted">
          <span>Página {page} de {paginas} · {data?.count.toLocaleString('es-CL')} ventas</span>
          <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} aria-label="Página anterior">
            <ChevronLeft size={14} />
          </Button>
          <Button variant="secondary" size="sm" disabled={page >= paginas} onClick={() => setPage(page + 1)} aria-label="Página siguiente">
            <ChevronRight size={14} />
          </Button>
        </div>
      )}
    </>
  )
}
